import React, { useRef, useState } from 'react'
import { IonLabel, IonItem, IonIcon, IonButton, IonButtons, IonChip, IonRow } from '@ionic/react'
import { IMultiSelectCustomInput, IOption, IOptions, IPNNVOption } from '../../common/common.types'
import './MultiSelect.component.css'
import { isTablet } from '../../utilities'
import MultiSelectModal from './MultiSelectModal/MultiSelectModal.component'
import MultiSelectPopover from './MultiSelectPopover/MultiSelectPopover.component'
import { useDispatch } from 'react-redux'
import { initializeSearch } from '../search/store/search.actions'
import { addCircleOutline, caretDownOutline, checkmarkSharp, closeCircleOutline } from 'ionicons/icons'
import { preventPageScroll } from '../../utilities/preventPageScroll.util'
import { getStyleForPHI, IPHIComponent } from '../phi/phi.component'
import ValidationBadgeComponent from '../validationBadge/ValidationBadge.component'

export interface IMultiSelectProps extends IPHIComponent {
  dataTestID: string
  savedValues: IOptions
  savedPNNVValue?: IPNNVOption | null
  label?: string
  action: (...args: any[]) => any
  pnnvAction?: (...args: any[]) => any
  callback?: (...args: any[]) => any
  dispatchData?: any
  options: IOptions
  listHeader?: string
  disabled?: boolean
  customInput?: IMultiSelectCustomInput
  showChips?: boolean
  reduxAction?: string
  showPNNVs?: boolean
  nemsis?: string
}
export const OTHER_OPTION_TEXT_BOX_VALUE_ID = null

const MultiSelect: React.FC<IMultiSelectProps> = (props: IMultiSelectProps) => {
  const {
    customInput,
    dataTestID,
    options,
    listHeader,
    action,
    pnnvAction,
    savedValues,
    savedPNNVValue = null,
    label,
    callback,
    dispatchData,
    disabled,
    isPHI,
    showChips = false,
    reduxAction,
    showPNNVs = true,
    nemsis = 'nemsisCode',
  } = props
  const isTabletDevice = isTablet()
  const dispatch = useDispatch()
  const [showModal, setShowModal] = useState(false)
  const [popoverState, setShowPopover] = useState({ showPopover: false, event: undefined })
  let selectedOptionNames: Array<string | undefined> = []
  const itemRef = useRef<HTMLIonItemElement>(null)

  if (savedValues.length) {
    selectedOptionNames = savedValues.filter((value) => value.name !== null).map((o) => o.name)
  }

  const showMultiSelectModal = (e: any) => {
    preventPageScroll(true)
    dispatch(initializeSearch(getOptions))
    if (isTabletDevice) {
      e.persist()
      setShowPopover({ showPopover: true, event: e })
    } else {
      setShowModal(true)
    }
  }

  const resetModalState = (showModal: boolean) => {
    setShowModal(showModal)
    preventPageScroll(false)
  }

  const resetPopoverState = (showPopover: boolean) => {
    setShowPopover({ showPopover, event: undefined })
    preventPageScroll(false)
  }

  const unselectOption = (item: IOption) => {
    const selectedData = savedValues.filter((option: IOption) => option.id !== item.id)
    dispatch(action(selectedData, dispatchData))
    if (callback) {
      dispatch(callback())
    }
  }

  const getPNNVOptions = !showPNNVs ? null : options.filter((data) => data.isNV || data.isPN)

  const getOptions = options.filter((data) => !data.isNV && !data.isPN)

  return (
    <div>
      <MultiSelectModal
        isPHI={isPHI}
        options={getOptions}
        pnnvOptions={getPNNVOptions}
        listHeader={listHeader}
        showModal={showModal}
        setShowModal={resetModalState}
        action={action}
        pnnvAction={pnnvAction}
        callback={callback}
        savedValues={savedValues}
        savedPNNVValue={savedPNNVValue}
        dispatchData={dispatchData}
        customInput={customInput}
        showPNNVs={showPNNVs}
        nemsis={nemsis}
      />

      <MultiSelectPopover
        isPHI={isPHI}
        itemRef={itemRef}
        event={popoverState.event}
        options={getOptions}
        pnnvOptions={getPNNVOptions}
        showModal={popoverState.showPopover}
        listHeader={listHeader}
        setShowModal={resetPopoverState}
        action={action}
        pnnvAction={pnnvAction}
        callback={callback}
        savedValues={savedValues}
        savedPNNVValue={savedPNNVValue}
        dispatchData={dispatchData}
        customInput={customInput}
        showPNNVs={showPNNVs}
        nemsis={nemsis}
      />

      {!showChips || savedPNNVValue ? (
        <IonItem
          ref={itemRef}
          button
          detail={false}
          className='multi-select-ion-item'
          disabled={disabled}
          data-testid={dataTestID}
          lines='none'
          onClick={showMultiSelectModal}>
          {reduxAction && <ValidationBadgeComponent action={reduxAction} />}
          {label && (
            <IonLabel
              position={savedPNNVValue || selectedOptionNames.length > 0 ? 'floating' : undefined}
              className={
                savedPNNVValue || selectedOptionNames.length > 0
                  ? 'multi-select-ion-label-floating'
                  : 'multi-select-ion-label'
              }
              color='medium'>
              {label}
            </IonLabel>
          )}
          {savedPNNVValue ? (
            <div data-private={isPHI} style={getStyleForPHI(isPHI)} className='selected-option ion-no-padding'>
              {savedPNNVValue.name}
            </div>
          ) : (
            selectedOptionNames.length > 0 && (
              <div data-private={isPHI} style={getStyleForPHI(isPHI)} className='selected-option ion-no-padding'>
                {selectedOptionNames.join(', ')}
              </div>
            )
          )}
          <IonIcon
            slot={label || selectedOptionNames ? 'end' : 'start'}
            size='small'
            color='medium'
            className='ion-icon-end'
            icon={caretDownOutline}
          />
        </IonItem>
      ) : (
        <>
          <IonItem
            ref={itemRef}
            button
            detail={false}
            className='multi-select-ion-item ion-no-padding'
            lines='none'
            onClick={showMultiSelectModal}
            disabled={disabled}
            data-testid={dataTestID}>
            <IonButtons>
              <IonButton className='font-size-16' data-testid={label} fill='clear' color='primary-alt'>
                <IonIcon className='mr-4' size='large' icon={addCircleOutline} />
                {label}
              </IonButton>
            </IonButtons>
            {savedValues.length > 2 && <IonIcon color='success' className='checkmark-icon' icon={checkmarkSharp} />}
          </IonItem>
          {savedValues.length > 0 && savedValues.length < 3 && (
            <IonItem lines='none' className='ml-10'>
              <IonRow className='ion-nowrap no-flex-shrink'>
                {savedValues.map((item) => (
                  <IonChip key={item?.id}>
                    <IonLabel>{item.name}</IonLabel>
                    <IonIcon
                      class='ion-align-self-center'
                      size='small'
                      icon={closeCircleOutline}
                      color='white'
                      onClick={() => {
                        unselectOption(item)
                      }}
                    />
                  </IonChip>
                ))}
              </IonRow>
            </IonItem>
          )}
        </>
      )}
    </div>
  )
}

export default MultiSelect
